import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { ArrowRight, Check, Mail, Sparkles, Loader2 } from "lucide-react";

const RULES_DE = [
  "Bewusstheit", "Verantwortung", "Selbst-Erkenntnis", "Gefühle dürfen sein",
  "Der Körper spricht", "Gedanken sind keine Wahrheit", "Das innere Kind",
  "Versöhnung", "Selbstwert ist eine Entscheidung", "Grenzen mit Liebe",
  "Spiegelgesetz I", "Spiegelgesetz II", "Liebe ist ein Handeln",
  "Sprache schafft Wirklichkeit", "Die Wurzel deiner Muster", "Glaubenssätze entlarven",
  "Schatten als Lehrer", "Der Atem · dein Anker", "Gegenwart · der einzige Ort",
  "Sinn entsteht durch Tun", "Berufung versus Beruf", "Geld ist Beziehung",
  "Familien-System · der unsichtbare Faden", "Generationen-Erbe lösen",
  "Entscheidung schafft Realität", "Konsequenz · den Weg gehen", "Beziehung als Spiegel",
  "Dankbarkeit als Frequenz", "Vermächtnis · was bleibt", "Love is Transformation",
];

const RULES_EN = [
  "Awareness", "Responsibility", "Self-recognition", "Feelings may be",
  "The body speaks", "Thoughts are not truth", "The inner child",
  "Reconciliation", "Self-worth is a decision", "Boundaries with love",
  "Mirror law I", "Mirror law II", "Love is action",
  "Language creates reality", "The root of your patterns", "Exposing core beliefs",
  "Shadow as teacher", "Breath · your anchor", "Presence · the only place",
  "Meaning emerges through doing", "Calling vs. career", "Money is relationship",
  "The family system · the invisible thread", "Releasing generational inheritance",
  "Decision creates reality", "Consequence · walking the path", "Relationship as mirror",
  "Gratitude as frequency", "Legacy · what remains", "Love is Transformation",
];

const COPY = {
  de: {
    kicker: "SAT · Gratis-Geschenk",
    t1: "DIE 30", t2: "Spielregeln", t3: "DES LEBENS.",
    lead: "30 Tage. 30 Impulse. Eine neue Beziehung zu dir selbst — kostenlos in dein Postfach.",
    intro: "Jeden Morgen eine Spielregel. Jede Spielregel ein Impuls, eine Erkenntnis, eine Praxis von 5–10 Minuten. Am Tag 30 lebst du anders — nicht weil du etwas gelernt hast, sondern weil du etwas erkannt hast.",
    free: "Kostenfrei", schedule: "30 Tage · 1 Mail pro Tag", unsub: "Jederzeit abmelden",
    form_kicker: "Dein Start in 30 Sekunden",
    first: "Dein Vorname", email: "Deine E-Mail-Adresse",
    cta: "Ja, ich will die 30 Spielregeln",
    consent: "Mit deiner Anmeldung stimmst du zu, dass wir dir die 30 Spielregeln-Sequenz sowie gelegentliche Inhalte der SAT-Akademie senden dürfen. Abmeldung mit einem Klick.",
    success_t: "Willkommen.",
    success_p: "Schau in dein Postfach — Tag 1 wartet schon. Falls die Mail nicht binnen einer Minute ankommt, prüfe bitte den Spam-Ordner.",
    success_cta: "Mehr über SAT Transformations Master™",
    overview_kicker: "Die 30 Tage im Überblick",
    overview_t: "Was du erhältst.",
    overview_p: "Jede Mail ist kurz. Jede Mail trifft. Keine Marketing-Floskeln.",
    why_kicker: "Warum diese Reihe?",
    why_t: "Weil eine Frage am Anfang von allem steht.",
    why_p1: "Die meisten Menschen wissen nicht, dass es Spielregeln gibt. Sie spielen das Spiel des Lebens — ohne zu wissen, dass es Regeln hat. Diese 30 Mails sind keine Lebensratgeber. Sie sind Hinweise auf etwas, das du immer schon gewusst hast — aber vergessen wolltest.",
    why_p2: "Wenn du nach den 30 Tagen tiefer gehen möchtest, wartet der ",
    err: "Fehler — bitte erneut versuchen.",
  },
  en: {
    kicker: "SAT · Free gift",
    t1: "THE 30", t2: "Rules", t3: "OF LIFE.",
    lead: "30 days. 30 impulses. A new relationship with yourself — free in your inbox.",
    intro: "Every morning a rule. Every rule an impulse, an insight, a 5–10 minute practice. On day 30 you live differently — not because you learned something, but because you recognised something.",
    free: "Free", schedule: "30 days · 1 mail per day", unsub: "Unsubscribe anytime",
    form_kicker: "Your start in 30 seconds",
    first: "Your first name", email: "Your email address",
    cta: "Yes, send me the 30 Rules",
    consent: "By signing up you agree that we may send you the 30 Rules sequence and occasional content from the SAT academy. One-click unsubscribe.",
    success_t: "Welcome.",
    success_p: "Check your inbox — day 1 is already waiting. If the mail doesn't arrive within a minute, please check your spam folder.",
    success_cta: "Discover SAT Transformations Master™",
    overview_kicker: "The 30 days at a glance",
    overview_t: "What you'll receive.",
    overview_p: "Every mail is short. Every mail lands. No marketing fluff.",
    why_kicker: "Why this series?",
    why_t: "Because one question stands at the beginning of everything.",
    why_p1: "Most people don't know there are rules. They play the game of life — without knowing it has rules. These 30 mails are not life-advice. They are hints toward something you've always known — and chose to forget.",
    why_p2: "After 30 days, if you wish to go deeper, the ",
    err: "Error — please try again.",
  },
};

const LeadMagnet30 = () => {
  const { lang } = useLang();
  const T = COPY[lang] || COPY.de;
  const rules = lang === "en" ? RULES_EN : RULES_DE;
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading"); setErr("");
    try {
      await api.post("/lead-magnet/30-spielregeln", { ...form, language: lang });
      setStatus("ok");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || T.err);
      setStatus("err");
    }
  };

  return (
    <div className="pt-24 min-h-screen" data-testid="lead-magnet-30">
      <section className="relative pt-16 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(212,175,55,0.18),transparent_55%),radial-gradient(circle_at_25%_70%,rgba(75,46,131,0.22),transparent_55%)]" />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4 flex items-center gap-2"><Sparkles size={14} className="text-[#D4AF37]" /> {T.kicker}</div>
            <h1 className="headline-caps text-[11vw] sm:text-[8vw] lg:text-[6rem]" style={{ lineHeight: 0.95 }}>
              <span className="block text-[#F7F6F2]">{T.t1}</span>
              <span className="block headline-italic text-[#D4AF37]" style={{ textTransform: "none", fontStyle: "italic", fontWeight: 500 }}>{T.t2}</span>
              <span className="block text-[#F7F6F2]">{T.t3}</span>
            </h1>
            <p className="serif italic text-2xl text-white/75 mt-8 max-w-xl">{T.lead}</p>
            <p className="text-white/65 leading-relaxed mt-6 max-w-xl">{T.intro}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 text-sm text-white/60">
              <div className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> {T.free}</div>
              <div className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> {T.schedule}</div>
              <div className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> {T.unsub}</div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="glass rounded-3xl p-8 md:p-10" data-testid="lead-magnet-form-card">
              {status === "ok" ? (
                <div className="text-center py-6" data-testid="lead-magnet-success">
                  <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37] text-[#081328] flex items-center justify-center mb-5"><Check size={26} /></div>
                  <h3 className="serif text-3xl font-light">{T.success_t}</h3>
                  <p className="text-white/70 mt-3 leading-relaxed">{T.success_p}</p>
                  <Link to="/programme/sat-master" className="btn-secondary mt-8 inline-flex">{T.success_cta} <ArrowRight size={14} /></Link>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="lead-magnet-form">
                  <div className="overline mb-2">{T.form_kicker}</div>
                  <input required className="sat-input" placeholder={T.first}
                    value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="lead-magnet-firstname" />
                  <input required type="email" className="sat-input" placeholder={T.email}
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="lead-magnet-email" />
                  <button type="submit" disabled={status === "loading"}
                    className="btn-primary w-full justify-center pulse-gold" data-testid="lead-magnet-submit">
                    {status === "loading" ? <Loader2 className="animate-spin" size={16} /> : <><Mail size={14} /> {T.cta}</>}
                  </button>
                  {err && <p className="text-red-300 text-xs">{err}</p>}
                  <p className="text-xs text-white/45 mt-3 leading-relaxed">{T.consent}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#050B1B]">
        <div className="container-sat max-w-[1100px]">
          <div className="overline mb-3">{T.overview_kicker}</div>
          <h2 className="headline-xl text-4xl md:text-6xl">{T.overview_t}</h2>
          <p className="serif italic text-xl text-white/55 mt-4 max-w-2xl">{T.overview_p}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-12">
            {rules.map((r, i) => (
              <div key={i} className="rounded-xl border border-white/8 bg-white/[0.02] p-4 hover:border-white/20 transition flex items-center gap-3" data-testid={`rule-preview-${i}`}>
                <span className="w-7 h-7 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="text-white/80 text-sm">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#081328]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="overline mb-3">{T.why_kicker}</div>
          <h2 className="headline-xl text-4xl md:text-5xl">{T.why_t}</h2>
          <p className="text-white/75 text-lg leading-relaxed mt-8 dropcap">{T.why_p1}</p>
          <p className="text-white/75 text-lg leading-relaxed mt-4">
            {T.why_p2}
            <Link to="/programme/sat-master" className="text-[#D4AF37] underline-offset-4 hover:underline">SAT Transformations Master™</Link>
            {lang === "en" ? " awaits you." : " wartet auf dich."}
          </p>
        </div>
      </section>
    </div>
  );
};

export default LeadMagnet30;
